// src/api/branchApi.ts
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { readFilteredEntity, createEntity, updateEntity, readEntity, deleteEntity } from '../services/crudService';
import { Branch } from '../interfaces/entities/branch';
import { Collection } from '../enums/collections.enum';
import { User } from '../interfaces/entities/user';
import { deleteStorageFile, getBranchImagePath } from '../services/storageService';
import { isOwnerOfResource } from '../hooks/useAuthenticatedUser';

export const branchApi = createApi({
  reducerPath: 'branchApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: [Collection.Branches],
  endpoints: (build) => ({
  
    getBranchById: build.query<Branch | null, string>({
      async queryFn(branchId: string) {
        try {
          const result = await readEntity<Branch>(Collection.Branches, branchId);
          if (result) {
            return { data: result };
          } else {
            return { data: null };
          }
        } catch (error) {
          return {
            error: {
              status: 500,
              message: (error as Error).message,
            }
          };
        }
      },
      providesTags: (result, error, id) => (result ? [{ type: Collection.Branches, id }] : []),
    }),

    getBranchesByCompanyId: build.query<Branch[], string>({
      async queryFn(companyId: string) {
        try {
          const result = await readFilteredEntity<Branch>(Collection.Branches, [
            { field: "companyId", operator: "==", value: companyId },
          ]);
          return { data: result || [] };
        } catch (error) {
          return {
            error: {
              status: 500,
              message: (error as Error).message,
            },
          };
        }
      },
      providesTags: [{ type: Collection.Branches, id: "LIST" }],
    }),

    updateBranch: build.mutation<void, { id: string; data: Branch }>({
      async queryFn({ id, data }, { getState }) {
        const state = getState() as { auth: { user: User | null } };
        const loggedInUser = state.auth?.user;

        if (isOwnerOfResource(data.companyId, loggedInUser)) {
          try {
            await updateEntity(Collection.Branches, id, data);
            return { data: undefined };
          } catch (error) {
            return {
              error: { status: 500, message: (error as Error).message },
            };
          }
        } else {
          return {
            error: { status: 500, message: 'Only owners allowed to update branches' },
          };
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: Collection.Branches, id },
        { type: Collection.Branches, id: "LIST" },
      ],
    }),

    createBranch: build.mutation<Branch, Branch>({
      async queryFn(branchData, { getState }) {
        const state = getState() as { auth: { user: User | null } };
        const loggedInUser = state.auth?.user;

        if (isOwnerOfResource(branchData.companyId, loggedInUser)) {
          try {
            const createdBranch = await createEntity(Collection.Branches, branchData);
            return { data: createdBranch as Branch };
          } catch (error) {
            return {
              error: { status: 500, message: (error as Error).message },
            };
          }
        } else {
          return {
            error: { status: 500, message: 'Only owners are allowed to create branches' },
          };
        }
      },
      invalidatesTags: [{ type: Collection.Branches, id: "LIST" }],
    }),

    getBranchByUrlKey: build.query<Branch | null, string>({
      async queryFn(urlKey) {
        try {
          const results = await readFilteredEntity<Branch>(Collection.Branches, [
            { field: "urlKey", operator: "==", value: urlKey },
          ]);
          return { data: results?.[0] ?? null };
        } catch (error) {
          return { error: { status: 500, message: (error as Error).message } };
        }
      },
    }),

    checkBranchUrlKeyAvailable: build.query<boolean, { urlKey: string; excludeBranchId?: string }>({
      async queryFn({ urlKey, excludeBranchId }) {
        try {
          const existing = await readFilteredEntity<Branch>(Collection.Branches, [
            { field: "urlKey", operator: "==", value: urlKey },
          ]);
          if (!existing || existing.length === 0) return { data: true };
          if (excludeBranchId) return { data: existing.every(b => b.id === excludeBranchId) };
          return { data: false };
        } catch (error) {
          return { error: { status: 500, message: (error as Error).message } };
        }
      },
    }),

    deleteBranch: build.mutation<void, Branch>({
      async queryFn(branch, { getState }) {
        const state = getState() as { auth: { user: User | null } };
        const loggedInUser = state.auth?.user;

        if (isOwnerOfResource(branch.companyId, loggedInUser)) {
          try {
            // Check if any users exist with this branchId
            const usersWithBranch = await readFilteredEntity<User>(Collection.Users, [
              { field: "branchId", operator: "==", value: branch.id },
            ]);

            if (usersWithBranch && usersWithBranch.length > 0) {
              return {
                error: { 
                  status: 400, 
                  message: `Cannot delete branch. ${usersWithBranch.length} user(s) are assigned to this branch. Please reassign or delete them first.` 
                },
              };
            }

            await deleteStorageFile(getBranchImagePath(branch.companyId, branch.id));
            await deleteEntity(Collection.Branches, branch.id);
            return { data: undefined };
          } catch (error) {
            return {
              error: { status: 500, message: (error as Error).message },
            };
          }
        } else {
          return {
            error: { status: 500, message: 'Only owners are allowed to delete branches' },
          };
        }
      },
      invalidatesTags: [{ type: Collection.Branches, id: "LIST" }],
    }),
  }),
});

export const { useGetBranchByIdQuery, useGetBranchesByCompanyIdQuery, useUpdateBranchMutation, useCreateBranchMutation, useDeleteBranchMutation, useLazyGetBranchByUrlKeyQuery, useLazyCheckBranchUrlKeyAvailableQuery } = branchApi;
